import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { toast } from '~/hooks/use-toast';

interface DateRange {
  from?: Date;
  to?: Date;
}

interface DateObject {
  range: DateRange;
  rangeCompare?: DateRange;
}

interface DateContextType {
  date: DateObject;
  setDate: (date: DateObject) => void;
}

const DateContext = createContext<DateContextType | undefined>(undefined);

const LOCAL_STORAGE_KEY = 'dateObject';

const getInitialFromTo = (): { from: Date; to: Date } => {
  const to = new Date();
  to.setDate(to.getDate() - 1);
  const from = new Date();
  from.setDate(from.getDate() - 8);
  return { from, to };
};

export const DateProvider = ({ children }: { children: React.ReactNode }) => {
  const [date, setDate] = useState<DateObject | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (saved) {
      try {
        const parsed = JSON.parse(saved) as DateObject;

        const range: DateRange = {
          from: parsed.range?.from ? new Date(parsed.range.from) : undefined,
          to: parsed.range?.to ? new Date(parsed.range.to) : undefined,
        };

        const rangeCompare: DateRange | undefined = parsed.rangeCompare
          ? {
              from: parsed.rangeCompare.from
                ? new Date(parsed.rangeCompare.from)
                : undefined,
              to: parsed.rangeCompare.to
                ? new Date(parsed.rangeCompare.to)
                : undefined,
            }
          : undefined;

        setDate({ range, rangeCompare });
        return;
      } catch (err) {
        toast({
          title: 'Error',
          description: 'Failed to load saved date.',
          variant: 'destructive',
          duration: 3000,
        });
      }
    }

    const { from, to } = getInitialFromTo();
    setDate({ range: { from, to } });
  }, []);

  useEffect(() => {
    if (date) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(date));
    }
  }, [date]);

  const updateDate = useCallback((newDate: DateObject) => {
    setDate(newDate);
  }, []);

  const value = useMemo(
    () =>
      date && {
        date,
        setDate: updateDate,
      },
    [date, updateDate],
  );

  if (!value) return null;

  return <DateContext.Provider value={value}>{children}</DateContext.Provider>;
};

export const useDate = () => {
  const context = useContext(DateContext);
  if (!context) {
    throw new Error('useDate must be used within a DateProvider');
  }
  return context;
};
